import React, { useEffect, useState } from 'react';
import {
    Popover,
    Select,
    MenuItem,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Button,
    TextField,
    InputAdornment,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
 
const CustomTimePicker = ({
    value = '',
    onChange,
    format = '24',
    label = 'Time',
    interval = 1,
    variant = 'outlined'
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const initialHour = value?.split(':')[0] || '00';
    const initialMinute = value?.split(':')[1] || '00';

    const [hour, setHour] = useState(initialHour);
    const [minute, setMinute] = useState(initialMinute);
    const [ampm, setAmpm] = useState('AM');

    const open = Boolean(anchorEl);

    const handleTriggerClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const generateOptions = (max, interval = 1) =>
        Array.from({ length: Math.ceil(max / interval) }, (_, i) =>
            String(i * interval).padStart(2, '0')
        );

    const handleOk = () => {
        let finalHour = hour;

        if (format === '12') {
            let h = parseInt(hour, 10);
            if (ampm === 'PM' && h < 12) h += 12;
            if (ampm === 'AM' && h === 12) h = 0;
            finalHour = String(h).padStart(2, '0');
        }

        const result = `${finalHour}:${minute}`;
        if (onChange) onChange(result);
        handleClose();
    };

    useEffect(() => {
        const [newHour = '00', newMinute = '00'] = value?.split(':') || [];
        setHour(newHour);
        setMinute(newMinute);
    }, [value]);

    const selectMenuProps = {
        PaperProps: {
            sx: {
                maxHeight: 220,
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }
        }
    };

    return (
        <>
            <TextField
                fullWidth
                variant={variant}
                label={label}
                value={value || ''}
                placeholder={`Select ${label}`}
                onClick={handleTriggerClick}
                InputProps={{
                    readOnly: true,
                    style: { cursor: 'pointer' },
                    endAdornment: (
                        <InputAdornment position="end">
                            <AccessTimeIcon fontSize="small" />
                        </InputAdornment>
                    )
                }}
                sx={{
                    '& .MuiInputBase-input': { cursor: 'pointer' }
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 260, bgcolor: '#ffffff' }}>
                    <Typography variant="subtitle2" fontWeight="600" color="textSecondary" sx={{ mb: -0.5 }}>
                        Select {label}
                    </Typography>

                    <Box display="flex" gap={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Hour</InputLabel>
                            <Select
                                value={hour}
                                label="Hour"
                                onChange={(e) => setHour(e.target.value)}
                                MenuProps={selectMenuProps}
                                sx={{ borderRadius: '8px' }}
                            >
                                {generateOptions(format === '12' ? 12 : 24).map((h) => {
                                    const val = h === '00' && format === '12' ? '12' : h;
                                    return (
                                        <MenuItem key={val} value={val}>
                                            {val}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel>Minute</InputLabel>
                            <Select
                                value={minute}
                                label="Minute"
                                onChange={(e) => setMinute(e.target.value)}
                                MenuProps={selectMenuProps}
                                sx={{ borderRadius: '8px' }}
                            >
                                {generateOptions(60, interval).map((m) => (
                                    <MenuItem key={m} value={m}>
                                        {m}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {format === '12' && (
                            <FormControl fullWidth size="small">
                                <InputLabel>AM/PM</InputLabel>
                                <Select
                                    value={ampm}
                                    label="AM/PM"
                                    onChange={(e) => setAmpm(e.target.value)}
                                    sx={{ borderRadius: '8px' }}
                                >
                                    <MenuItem value="AM">AM</MenuItem>
                                    <MenuItem value="PM">PM</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </Box>

                    <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                        <Button 
                            variant="text" 
                            onClick={handleClose} 
                            color="inherit" 
                            sx={{ borderRadius: '8px', px: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleOk} 
                            color="primary"
                            disableElevation
                            sx={{ borderRadius: '8px', px: 3, fontWeight: '600' }}
                        >
                            OK
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default CustomTimePicker;
 
 